import { Compass, Brain, ClipboardCheck, Users, GitBranch, Sparkles, BarChart3, CalendarCheck, Target, Activity, Network, Moon, Sunrise, Music } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-12 text-slate-300">
      <div className="text-center mb-10">
        <Compass size={36} className="text-blue-400 mx-auto mb-3" />
        <h1 className="text-display font-display font-bold bg-brand-gradient bg-clip-text text-transparent">The Navigator</h1>
        <p className="text-sm text-slate-300 mt-2 leading-snug">The mentor who remembers your word — and won't let you off the hook.</p>
        <p className="text-[11px] text-slate-600 mt-2 uppercase tracking-[0.2em]">Clarity · Structure · Accountability</p>
      </div>

      <section className="mb-8">
        <p className="text-sm leading-relaxed">
          The Navigator is a persistent AI mentor for people navigating career disruption,
          economic uncertainty, and life transition. It combines the directness of a good
          coach with the memory of a long-term relationship — remembering what you said
          last week, tracking the commitments you made, and not letting you off the hook.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-sm font-semibold text-slate-200 mb-4 uppercase tracking-wide">Core Thesis</h2>
        <p className="text-sm leading-relaxed text-slate-400">
          Most people navigating change don't need more information — they need structured
          accountability and a push toward action that creates value for others.
          Generic advice fails because it could apply to anyone. The Navigator addresses
          your actual situation and holds you to your own stated goals.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-sm font-semibold text-slate-200 mb-4 uppercase tracking-wide">Three Pillars</h2>
        <div className="space-y-4">
          <div className="flex gap-3 items-start">
            <Brain size={18} className="text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-slate-200">Persistent Memory</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Sessions build on each other. The Navigator tracks your patterns,
                commitments, and progress across conversations — so you never start from zero.
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <ClipboardCheck size={18} className="text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-slate-200">Structured Accountability</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Every session ends with a concrete assignment — a clear goal, completion
                criteria, a deadline, and who it helps. The next session opens with a check-in.
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <Users size={18} className="text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-slate-200">Outward Orientation</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Assignments progress from preparation to delivery — creating tangible value
                for at least one other person. Momentum comes from contribution, not introspection alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-sm font-semibold text-slate-200 mb-4 uppercase tracking-wide">Daily Planning</h2>
        <p className="text-sm leading-relaxed text-slate-400 mb-4">
          Each day starts with an AI-guided planning session. For every action, you
          define the result you want and why it matters — so your day is driven by
          purpose, not obligation.
        </p>
        <div className="space-y-4">
          <div className="flex gap-3 items-start">
            <Target size={18} className="text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-slate-200">Commitments vs. Momentum Builders</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Your daily actions split into non-negotiable commitments — the 3-5 things
                that mean you've won the day — and momentum builders that push you past 80%
                completion. Hit your commitments and you've won. Everything else is bonus fuel.
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <Sparkles size={18} className="text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-slate-200">AI-Informed Suggestions</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                The Navigator reviews your past 7 days — completion rates, deferred items,
                neglected threads — and suggests actions you may have missed. You accept or
                decline each one. It references what actually worked, not generic advice.
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <CalendarCheck size={18} className="text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-slate-200">Evening Review</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                End each day with a brief review — celebrate what got done, acknowledge
                what didn't, and carry forward anything that still matters. Your streak
                and completion rate track real momentum over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-sm font-semibold text-slate-200 mb-4 uppercase tracking-wide">Deep Rest</h2>
        <p className="text-sm leading-relaxed text-slate-400 mb-4">
          Accountability on its own is all stick. Deep Rest is the carrot, made from the same
          promise: a guided rest and rehearsal, voiced in the voice you chose over original
          ambient music, built from your own commitments and the excuses you predicted. It is
          mental contrasting in a relaxed state: picture the outcome, name the obstacle,
          rehearse the plan. Not positive thinking, and never the same session twice.
        </p>
        <div className="space-y-4">
          <div className="flex gap-3 items-start">
            <Sunrise size={18} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-slate-200">Morning Prime</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Made the moment you finish planning: about six minutes that rehearse today's
                first action, the excuse most likely before noon, and what you do when it shows
                up. It ends with energy, not sleep.
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <Moon size={18} className="text-indigo-300 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-slate-200">Evening Rest</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Made after the evening review: about twelve minutes. What got done is named
                once. What didn't is named without a ledger. Tomorrow's first step is rehearsed
                in your words until it feels decided, then the music carries you out.
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <Music size={18} className="text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-slate-200">Yours, and never repeated</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Sixty ways in, a bank of original music, sessions for one life area at a time,
                a five, ten, or fifteen minute length, and one-tap tuning that makes the next one
                slower or the music quieter. Read every word, keep the ones you love, download
                for offline listening.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-sm font-semibold text-slate-200 mb-4 uppercase tracking-wide">State Check-In</h2>
        <div className="flex gap-3 items-start">
          <Activity size={18} className="text-blue-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm leading-relaxed text-slate-400">
              Before each session, rate your energy, certainty, and clarity on a 1-10 scale.
              If you're running low, the Navigator offers a guided breathing exercise and
              gratitude priming to help you show up at your best. These scores travel with
              your session so the AI can adapt its approach to where you actually are — not
              where you wish you were.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-sm font-semibold text-slate-200 mb-4 uppercase tracking-wide">Life Threads</h2>
        <p className="text-sm leading-relaxed text-slate-400 mb-4">
          Life doesn't fit into one conversation. Life Threads let you organize your
          Navigator sessions by area — career, health, family, finances, or whatever
          matters to you — each with its own assignments, patterns, and history.
        </p>
        <div className="space-y-4">
          <div className="flex gap-3 items-start">
            <GitBranch size={18} className="text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-slate-200">Separate Conversations, Shared Context</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Each thread maintains its own chat history, assignments, and patterns. Your
                global context — situation, skills, runway — stays shared so the Navigator
                always knows the full picture.
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <Sparkles size={18} className="text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-slate-200">Cross-Thread Intelligence</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                The Navigator watches across all your threads for patterns you might miss —
                overcommitting in multiple areas, avoiding a thread while others stay active,
                or momentum building across your whole life. It will also suggest creating
                a new thread when you bring up something that deserves its own space.
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <BarChart3 size={18} className="text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-slate-200">Holistic Progress</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                The Progress tab can show you a single thread's assignments and sessions,
                or toggle to an all-threads overview to see where you're moving and where
                you've stalled. Double-click any thread name to rename it.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-sm font-semibold text-slate-200 mb-4 uppercase tracking-wide">Your Map</h2>
        <div className="flex gap-3 items-start">
          <Network size={18} className="text-blue-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm leading-relaxed text-slate-400">
              An interactive map of your life threads, behavioral patterns, and assignments —
              and the connections between them. See which threads are active, where patterns
              cluster, and how cross-thread insights link different areas of your life.
              Available on Pro and Entrepreneur plans.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-sm font-semibold text-slate-200 mb-4 uppercase tracking-wide">Guided Onboarding</h2>
        <p className="text-sm leading-relaxed text-slate-400">
          Your first session is a guided discovery conversation. The Navigator learns about
          your situation, goals, and needs — then automatically creates personalized life
          threads for the areas that matter most to you. Two minutes of signup, then you
          talk. No setup wizard — just a conversation that builds your foundation.
        </p>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-200 mb-4 uppercase tracking-wide">Who It's For</h2>
        <p className="text-sm leading-relaxed text-slate-400">
          Anyone navigating real change — career transition, financial rebuilding, health
          goals, relationship work, or all of the above at once. If you want gentle
          encouragement without follow-through, this isn't it. If you want a mentor who
          remembers what you committed to, tracks your patterns across every area of your
          life, and evaluates outcomes — not just effort — this is.
        </p>
      </section>
    </div>
  );
}
